// packages/frontend/src/features/tonight/TonightDinnerCard.tsx
//
// Tonight page (Plan 12): today's dinner, front and center. Never a dead end —
// every state (no plan / skipped / empty / recipe / freetext / leftover) ends
// with either a "Start cooking" action or a "This week →" escape hatch into
// the full planner. Mirrors the visual idiom of `meal-plans/TodayCard.tsx`
// (amber start-by alert, mealSummary-style secondary row) and
// `cook-sessions/ActiveCooksCard.tsx` (Card/CardHeader shell, row hover).
import type { JSX } from "react";
import { Link } from "react-router";
import {
  buildSlotIndex,
  resolveLeftoverLabel,
  type RecipeApiItem,
  type Slot,
  type TodayDigest,
} from "@recipevault/shared";
import { Card, CardHeader, CardTitle, CardContent } from "@/ui/card.js";
import { Button } from "@/ui/button.js";
import { RecipePhotoImg } from "../recipes/RecipePhotoImg.js";
import { CoverPlaceholder } from "../recipes/CoverPlaceholder.js";
import { resolveItemLabel } from "../meal-plans/resolveItem.js";
import { MEAL_LABELS } from "../meal-plans/mealLabels.js";
import { useAuth } from "../auth/AuthProvider.js";
import type { TonightData } from "./useTonight.js";

interface TonightDinnerCardProps {
  data: TonightData;
  /** Parent owns session-create + navigate (mirrors `RecipeView.startCooking`). */
  onStartCooking: (recipeId: string) => void;
}

/** "Tomato Soup, Leftover: Chili" style summary — same idiom as `TodayCard.mealSummary`. */
function mealSummary(meal: TodayDigest["meals"][number], recipeTitles: Map<string, string>): string {
  const labels = meal.items.map((item) => resolveItemLabel(item, recipeTitles));
  if (meal.leftoverLabel) labels.push(`Leftover: ${meal.leftoverLabel}`);
  return labels.length > 0 ? labels.join(", ") : (meal.note ?? "—");
}

function EmptyDinnerRow({ weekStart }: { weekStart: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-zinc-500">No dinner planned</span>
      <Link
        to={`/plan/${weekStart}`}
        className="shrink-0 whitespace-nowrap text-xs text-emerald-400 hover:text-emerald-300"
      >
        This week →
      </Link>
    </div>
  );
}

interface DinnerSlotRowProps {
  slot: Slot;
  weekStart: string;
  householdId: string;
  recipesById: Map<string, RecipeApiItem>;
  recipeTitles: Map<string, string>;
  slotIndex: Map<string, Slot>;
  onStartCooking: (recipeId: string) => void;
}

/** One dinner slot, rendered per the rule ladder: skipped → empty → leftover → recipe → freetext. */
function DinnerSlotRow({
  slot,
  weekStart,
  householdId,
  recipesById,
  recipeTitles,
  slotIndex,
  onStartCooking,
}: DinnerSlotRowProps): JSX.Element {
  if (slot.skipped === true) {
    return (
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-zinc-600 line-through">Dinner skipped</span>
        <Link
          to={`/plan/${weekStart}`}
          className="shrink-0 whitespace-nowrap text-xs text-emerald-400 hover:text-emerald-300"
        >
          This week →
        </Link>
      </div>
    );
  }

  const isEmpty = slot.items.length === 0 && slot.leftoverOf === undefined;
  if (isEmpty) {
    return <EmptyDinnerRow weekStart={weekStart} />;
  }

  if (slot.leftoverOf !== undefined) {
    const label = resolveLeftoverLabel(slot, slotIndex, {
      recipeTitle: (id) => recipesById.get(id)?.title ?? null,
    });
    return <p className="text-sm text-zinc-200">Leftovers · {label ?? "Leftover"}</p>;
  }

  const [primary, ...rest] = slot.items;
  // Unreachable given the isEmpty check above, but keeps this component total.
  if (!primary) return <EmptyDinnerRow weekStart={weekStart} />;

  const extra =
    rest.length > 0 ? (
      <p className="text-xs text-zinc-500">+ {rest.map((item) => resolveItemLabel(item, recipeTitles)).join(", ")}</p>
    ) : null;

  if (primary.kind === "recipe") {
    const recipe = recipesById.get(primary.recipeId);
    const title = recipe?.title ?? "Recipe";
    return (
      <div className="flex flex-col gap-2">
        <Link
          to={`/recipes/${primary.recipeId}`}
          className="-mx-1 flex items-center gap-3 rounded-lg px-1 py-1 transition-colors hover:bg-zinc-800/60"
        >
          {recipe?.photos.coverKey ? (
            <RecipePhotoImg
              householdId={householdId}
              recipeId={primary.recipeId}
              photoKey={recipe.photos.coverKey}
              size="thumb"
              alt={`Cover for ${title}`}
              className="h-12 w-12 shrink-0 rounded-md object-cover"
            />
          ) : (
            <CoverPlaceholder
              seed={primary.recipeId}
              title={title}
              className="h-12 w-12 shrink-0 rounded-md"
              letterClassName="text-xl"
            />
          )}
          <span className="truncate font-medium text-zinc-50">{title}</span>
        </Link>
        {extra}
        <Button
          size="cook"
          className="w-full"
          onClick={() => {
            onStartCooking(primary.recipeId);
          }}
        >
          Start cooking
        </Button>
      </div>
    );
  }

  // freetext
  return (
    <div className="flex flex-col gap-1">
      <div className="text-sm text-zinc-200">
        <span>{resolveItemLabel(primary, recipeTitles)}</span>
        {primary.reference && <span className="ml-1.5 text-xs text-zinc-500">{primary.reference}</span>}
      </div>
      {extra}
    </div>
  );
}

export function TonightDinnerCard({ data, onStartCooking }: TonightDinnerCardProps): JSX.Element {
  const { me } = useAuth();
  const householdId = me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";

  let content: JSX.Element;
  if (data.plan === null) {
    content = (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-zinc-400">Nothing planned this week</p>
        <Button asChild size="cook" className="w-full">
          <Link to="/plan">Plan this week</Link>
        </Button>
      </div>
    );
  } else {
    const { plan, digest, todayDay, recipesById, weekStart } = data;
    const recipeTitles = new Map(Array.from(recipesById, ([id, r]) => [id, r.title]));
    const slotIndex = buildSlotIndex(plan);
    const dinnerSlots = (todayDay?.slots ?? []).filter((s) => s.mealType === "dinner");
    const secondaryMeals = (digest?.meals ?? []).filter((m) => m.mealType !== "dinner");
    const startByWarnings = digest?.startByWarnings ?? [];

    content = (
      <>
        {startByWarnings.length > 0 && (
          <div
            role="alert"
            className="flex flex-col gap-0.5 rounded-lg border border-amber-700 bg-amber-950/40 p-2 text-sm text-amber-300"
          >
            {startByWarnings.map((task) => (
              <p key={task.id}>
                Start &ldquo;{task.title}&rdquo; by {task.startByTime}
              </p>
            ))}
          </div>
        )}

        {dinnerSlots.length > 0 ? (
          dinnerSlots.map((slot) => (
            <DinnerSlotRow
              key={slot.id}
              slot={slot}
              weekStart={weekStart}
              householdId={householdId}
              recipesById={recipesById}
              recipeTitles={recipeTitles}
              slotIndex={slotIndex}
              onStartCooking={onStartCooking}
            />
          ))
        ) : (
          <EmptyDinnerRow weekStart={weekStart} />
        )}

        {secondaryMeals.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1 border-t border-zinc-800 pt-2">
            {secondaryMeals.map((meal) => (
              <li key={meal.slotId} className="text-sm text-zinc-200">
                <span className="font-medium text-zinc-400">{MEAL_LABELS[meal.mealType]}: </span>
                {mealSummary(meal, recipeTitles)}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <section aria-label="Tonight's dinner">
      <Card>
        <CardHeader>
          <CardTitle>Tonight&rsquo;s dinner</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">{content}</CardContent>
      </Card>
    </section>
  );
}
